import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslate } from "@refinedev/core";

import type { RondleidingStap } from "./rondleidingen";

/**
 * Begeleiding in het scherm zelf: een spot op de knop waar je moet zijn, met een uitleg ernaast.
 *
 * <p>De aanleiding was een geschreven lijstje met stappen. Dat leest als een handleiding en laat je
 * vervolgens alleen de knop zoeken. Dit wijst hem aan, op jouw eigen scherm en met jouw eigen
 * gegevens — en het blijft kloppen als er een knop verplaatst, want er wordt gezocht op wat er
 * staat en niet op een plaatje van vroeger.
 *
 * <p><b>Altijd te stoppen.</b> Escape, het kruisje en de knop Sluiten doen alle drie hetzelfde, op
 * elk moment. Begeleiding die je niet kwijtraakt is geen hulp maar een gijzeling; daarom staat de
 * uitweg overal en hoef je hem nergens te zoeken.
 *
 * <p><b>Het donker vangt geen klikken.</b> Eerder deed het dat wel — het bestond uit panelen die
 * over het scherm lagen — en dus sloot de rondleiding zodra je ergens anders klikte. In een wizard
 * is dat verkeerd om: daar moet je juist klikken. Het donker is nu alleen nog een schaduw en de
 * app blijft er gewoon onder werken. Drie zichtbare uitwegen zijn genoeg; per ongeluk wegklikken
 * hoort er geen vierde te zijn.
 */
export function Rondleiding({
  stappen,
  onSluiten,
  onNavigeer,
}: {
  stappen: RondleidingStap[];
  onSluiten: () => void;
  onNavigeer: (route: string) => void;
}) {
  const t = useTranslate();
  const [index, setIndex] = useState(0);
  const [vak, setVak] = useState<DOMRect | null>(null);
  const [gevonden, setGevonden] = useState(true);
  /**
   * Welke stap al is overgeslagen. Zonder deze grendel sloeg de rondleiding er twee tegelijk over:
   * er kan meer dan één zoeklus tegelijk lopen (een oude die nog een tik openstond, en de nieuwe),
   * en die verhoogden allebei de teller. Je kwam dan uit op een stap waarvan het doel nog in een
   * dichtgeklapte groep zat — grijs scherm, geen pijl, geen weg vooruit.
   */
  const overgeslagen = useRef<number | null>(null);

  const stap = stappen[index];
  const laatste = index === stappen.length - 1;

  /** Het element van deze stap: op selector, en als er meer treffers zijn op zichtbare tekst. */
  const zoekDoel = useCallback((): HTMLElement | null => {
    if (!stap) return null;
    const kandidaten = [...document.querySelectorAll<HTMLElement>(stap.selector)].filter((el) => {
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return false;
      // Buiten beeld is net zo onbruikbaar als afwezig: de dichtgeklapte lade staat links van het
      // venster, en daar een spot op zetten wijst nergens naar.
      return r.right > 0 && r.left < window.innerWidth;
    });
    if (kandidaten.length === 0) return null;
    if (!stap.bevat) return kandidaten[0];
    const zoek = stap.bevat.toLowerCase();
    // Ook op aria-label en title, niet alleen op zichtbare tekst. Een knop met alleen een icoon
    // heeft geen tekst — "Bewerken" op het tariefscherm is er zo een — en die werd dus nooit
    // gevonden, terwijl hij voor een schermlezer wél gewoon "Bewerken" heet.
    const naam = (el: HTMLElement) =>
      [el.textContent, el.getAttribute("aria-label"), el.getAttribute("title")]
        .filter(Boolean).join(" ").toLowerCase();
    return kandidaten.find((el) => naam(el).includes(zoek)) ?? null;
  }, [stap]);

  // Naar het juiste scherm voordat er gezocht wordt; anders wijst de spot naar een vorig scherm.
  useEffect(() => {
    if (stap?.route) onNavigeer(stap.route);
    // onNavigeer is stabiel bij de aanroeper; alleen de stap hoort dit opnieuw te doen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  useLayoutEffect(() => {
    let afgebroken = false;
    let timer = 0;
    // Bij elke stap eerst de spot weghalen. Blijft hij staan terwijl het nieuwe doel nog niet
    // gevonden is, dan wijst hij naar het vorige element — en dan klikt iemand precies verkeerd.
    setVak(null);
    setGevonden(true);

    let pogingen = 0;
    const meet = () => {
      if (afgebroken) return;
      const doel = zoekDoel();
      if (doel) {
        // Meteen scrollen en niet vloeiend. Bij een vloeiende scroll schuift het element nog weg
        // terwijl de spot al gemeten is, en dan staat de rand een halve seconde om iets anders
        // heen — gezien gebeuren: de kaart zei "Asieltarieven" terwijl de spot om Uitloggen lag.
        // Een lijst waarin je moet klikken mag nooit de verkeerde knop aanwijzen, ook niet even.
        doel.scrollIntoView({ block: "center", behavior: "auto" });
        // Meteen meten, zodat er altijd een spot staat — ook in een tabblad op de achtergrond,
        // waar requestAnimationFrame niet vuurt. En daarna nog een keer op het volgende beeld,
        // want de scroll kan het element nog een paar pixels verzetten.
        setVak(doel.getBoundingClientRect());
        setGevonden(true);
        requestAnimationFrame(() => {
          if (afgebroken) return;
          setVak(doel.getBoundingClientRect());
        });
        return;
      }
      // Een stap die van het toestel afhangt heeft geen zin om op te wachten. Eén keer per stap.
      if (stap?.overslaanAlsAfwezig) {
        if (overgeslagen.current !== index) {
          overgeslagen.current = index;
          if (index < stappen.length - 1) {
            setIndex(index + 1);
          } else {
            onSluiten();
          }
        }
        return;
      }
      pogingen += 1;
      // Blijf zoeken zolang de stap loopt. Het doel verschijnt vaak pas door iets wat de gebruiker
      // zelf doet — een lade openen, een groep openklappen, een pagina die nog laadt — en dat duurt
      // net zo lang als het duurt. Na twaalf pogingen zegt de kaart alvast dat het er nog niet is,
      // maar het zoeken gaat door, zodat hij alsnog aanwijst zodra het verschijnt.
      // Na ruim een seconde al zeggen dat het er nog niet is. Stil blijven terwijl er niets
      // gebeurt laat iemand denken dat de rondleiding kapot is.
      if (pogingen === 4) {
        setGevonden(false);
      }
      timer = window.setTimeout(meet, 400);
    };
    meet();

    const opnieuw = () => {
      const doel = zoekDoel();
      if (doel) setVak(doel.getBoundingClientRect());
    };
    window.addEventListener("scroll", opnieuw, true);
    window.addEventListener("resize", opnieuw);
    return () => {
      afgebroken = true;
      window.clearTimeout(timer);
      window.removeEventListener("scroll", opnieuw, true);
      window.removeEventListener("resize", opnieuw);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoekDoel, index]);

  // Doe je wat er gevraagd wordt, dan gaat de rondleiding vanzelf mee. Dat is het verschil met
  // een lijstje: je leest niet wat je moet doen, je dóét het en de uitleg loopt met je mee.
  useEffect(() => {
    const doel = zoekDoel();
    if (!doel) return;
    const opKlik = () => {
      // Even wachten: het scherm moet eerst reageren (een groep openklappen, een pagina laden).
      window.setTimeout(() => setIndex((i) => Math.min(i + 1, stappen.length - 1)), 350);
    };
    doel.addEventListener("click", opKlik, { once: true });
    return () => doel.removeEventListener("click", opKlik);
  }, [zoekDoel, stappen.length, vak]);

  // Stoppen kan altijd, ook met het toetsenbord.
  useEffect(() => {
    const opToets = (e: KeyboardEvent) => {
      if (e.key === "Escape") onSluiten();
    };
    window.addEventListener("keydown", opToets);
    return () => window.removeEventListener("keydown", opToets);
  }, [onSluiten]);

  if (!stap) return null;

  // De uitleg staat onder het aangewezen element, of erboven als daar geen plek is. Zonder doel
  // staat hij in het midden: dan is er niets aan te wijzen en is de tekst het enige dat telt.
  const marge = 22;
  const kaartBreedte = Math.min(360, window.innerWidth - 24);
  // Onder het doel als daar plek is, anders erboven. De pijl volgt dezelfde keuze.
  const kaartOnder = !!vak && vak.bottom + marge + 200 <= window.innerHeight;
  const stijl: React.CSSProperties = vak
    ? {
        top: kaartOnder ? vak.bottom + marge : undefined,
        bottom: kaartOnder ? undefined : window.innerHeight - vak.top + marge,
        left: Math.max(12, Math.min(vak.left, window.innerWidth - kaartBreedte - 12)),
        width: kaartBreedte,
      }
    : { top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: kaartBreedte };

  return (
    <div className="fawkes-rondleiding" role="dialog" aria-modal="true" aria-label={stap.titel}>
      {/* Eén uitsnede, geen vier panelen. De vorige versie legde vier rechthoeken om het doel heen
          en liet daartussen een gat vrij; dat gaf harde hoeken, zichtbare naden waar de panelen
          elkaar raakten, en het kon niet meebewegen. Nu is het één vak met een schaduw die het hele
          scherm beslaat: buiten het vak wordt alles donker, het vak zelf blijft precies zoals het
          is, en omdat het één element is kan het van stap naar stap glijden in plaats van te
          springen. Ronde hoeken komen er gratis bij. */}
      {vak && (
        <div
          className="fawkes-rondleiding__spot"
          style={{ top: vak.top - 8, left: vak.left - 8,
                   width: vak.width + 16, height: vak.height + 16 }}
        />
      )}

      {/* De pijl zit aan de kaart en niet los in het scherm: hij hoort bij de uitleg en wijst
          daarvandaan naar de plek. Los zwevend was hij een derde ding om te ontcijferen. */}
      {vak && (
        <div
          className={`fawkes-rondleiding__pijl fawkes-rondleiding__pijl--${kaartOnder ? "omhoog" : "omlaag"}`}
          style={{
            left: Math.min(Math.max(vak.left + vak.width / 2 - 9, 16), window.innerWidth - 34),
            top: kaartOnder ? vak.bottom + 10 : undefined,
            bottom: kaartOnder ? undefined : window.innerHeight - vak.top + 10,
          }}
        />
      )}

      <div className="fawkes-rondleiding__kaart" style={stijl}>
        <div className="fawkes-rondleiding__kop">
          {/* Bolletjes en geen "Stap 5 van 6". Op het inboekscherm staat de wizard zijn eigen
              stappen al genummerd te tonen, en twee tellers naast elkaar die niet hetzelfde
              betekenen leest als een fout. Bolletjes zeggen hetzelfde zonder mee te tellen. */}
          <span
            className="fawkes-rondleiding__bolletjes"
            aria-label={t("rondleiding.stapVan", { args: [index + 1, stappen.length] })}
          >
            {stappen.map((_, i) => (
              <i
                key={i}
                className={
                  "fawkes-rondleiding__bol"
                  + (i === index ? " fawkes-rondleiding__bol--nu" : "")
                  + (i < index ? " fawkes-rondleiding__bol--gehad" : "")
                }
              />
            ))}
          </span>
          <button
            type="button"
            className="fawkes-rondleiding__sluit"
            onClick={onSluiten}
            aria-label={t("rondleiding.sluiten")}
          >
            ✕
          </button>
        </div>
        <p className="fawkes-rondleiding__titel">{stap.titel}</p>
        <p className="fawkes-rondleiding__tekst">{stap.tekst}</p>
        {!gevonden && (
          <>
            <p className="fawkes-rondleiding__nietGevonden">{t("rondleiding.nietGevonden")}</p>
            {/* Zonder doel dekt de rondleiding niets af, dus je kunt gewoon doorklikken in de app.
                Zodra het element verschijnt springt de spot er vanzelf op. */}
          </>
        )}
        <div className="fawkes-rondleiding__knoppen">
          <button type="button" className="fui-button fui-button--secondary" onClick={onSluiten}>
            {t("rondleiding.sluiten")}
          </button>
          {index > 0 && (
            <button
              type="button"
              className="fui-button fui-button--secondary"
              onClick={() => setIndex((i) => i - 1)}
            >
              {t("rondleiding.vorige")}
            </button>
          )}
          <button
            type="button"
            className="fui-button fui-button--primary"
            onClick={() => (laatste ? onSluiten() : setIndex((i) => i + 1))}
          >
            {laatste ? t("rondleiding.klaar") : t("rondleiding.volgende")}
          </button>
        </div>
      </div>
    </div>
  );
}
